import {
  AsYouType,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
  type CountryCode,
  type Examples,
  type ValidatePhoneNumberLengthResult,
} from 'libphonenumber-js/min';
// SIL-1212 — example-number metadata for realistic placeholders (~4KB
// uncompressed, ~2KB gzip'd — negligible next to the ~85KB /min metadata
// this module already pulls in). One JSON module, shared by every country;
// no per-country hand-written examples.
import phoneExamples from 'libphonenumber-js/examples.mobile.json';

/**
 * SIL-1198 — individual profile phone: format as you type with a dial-code
 * selector, store E.164. Pure logic only (no React) so both `apps/api`
 * (validate + normalize on write) and `apps/customer` (live formatting +
 * client-side validation) share ONE implementation of "what counts as a
 * valid phone number" — per the issue's decision, this package never
 * hand-writes country formatting/validation rules; `libphonenumber-js`
 * (`/min` build, per the issue) owns all of that.
 */
export type { CountryCode };

/** True when `code` is one `libphonenumber-js` actually has metadata for. */
export function isSupportedPhoneCountry(code: string): code is CountryCode {
  return (SUPPORTED_COUNTRY_SET as Set<string>).has(code);
}

const SUPPORTED_COUNTRY_SET: ReadonlySet<CountryCode> = new Set(getCountries());

/** ISO 3166-1 alpha-2 → flag emoji via the regional-indicator-symbol trick. */
function flagEmoji(code: CountryCode): string {
  return code
    .toUpperCase()
    .replace(/./g, (ch) => String.fromCodePoint(127397 + ch.charCodeAt(0)));
}

function countryDisplayName(code: CountryCode): string {
  if (typeof Intl.DisplayNames !== 'function') return code;
  const names = new Intl.DisplayNames(['en'], { type: 'region', fallback: 'code' });
  return names.of(code) ?? code;
}

export interface PhoneDialCodeOption {
  country: CountryCode;
  name: string;
  callingCode: string;
  flag: string;
  /**
   * e.g. "🇺🇸 +1 United States" — what the <select>'s <option> text shows.
   * SIL-1212: the flag + dial code come FIRST, before the (often much
   * longer) country name, so a narrow closed <select> box — which only has
   * room to show the start of the selected option's text — still shows the
   * dial code in full instead of truncating it away behind the name.
   */
  label: string;
}

/**
 * Every country `libphonenumber-js` has metadata for, sorted by display
 * name — backs the "Country dial code" selector. Computed once at module
 * load (mirrors individual-profile.tsx's existing COUNTRY_OPTIONS pattern
 * for countryOfResidence).
 */
export const PHONE_DIAL_CODE_OPTIONS: PhoneDialCodeOption[] = getCountries()
  .map((country) => {
    const name = countryDisplayName(country);
    const callingCode = getCountryCallingCode(country);
    return {
      country,
      name,
      callingCode,
      flag: flagEmoji(country),
      label: `${flagEmoji(country)} +${callingCode} ${name}`,
    };
  })
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * SIL-1212 — pinned at the top of the "Country dial code" selector, above
 * the full alphabetical list, so the handful of countries most of
 * SiliconX's customer base actually uses aren't buried in a ~245-entry
 * list. Order here is the pinned display order (not alphabetical).
 */
export const PHONE_DIAL_CODE_COMMON_COUNTRIES: CountryCode[] = [
  'US',
  'CA',
  'GB',
  'AU',
  'DE',
  'FR',
  'IN',
  'MX',
];

const COMMON_COUNTRY_SET = new Set<CountryCode>(PHONE_DIAL_CODE_COMMON_COUNTRIES);

/** Pinned countries (in `PHONE_DIAL_CODE_COMMON_COUNTRIES` order), for the top of the selector. */
export const PHONE_DIAL_CODE_COMMON_OPTIONS: PhoneDialCodeOption[] = PHONE_DIAL_CODE_COMMON_COUNTRIES.map(
  (country) => PHONE_DIAL_CODE_OPTIONS.find((o) => o.country === country),
).filter((o): o is PhoneDialCodeOption => o != null);

/** Every other country (alphabetical), for the selector's second, unpinned group. */
export const PHONE_DIAL_CODE_OTHER_OPTIONS: PhoneDialCodeOption[] = PHONE_DIAL_CODE_OPTIONS.filter(
  (o) => !COMMON_COUNTRY_SET.has(o.country),
);

/**
 * Best-effort default dial country, in priority order: (1) the number
 * itself, if it already parses as international (`+…`, e.g. a legacy
 * value that happens to be well-formed); (2) the supplied address country,
 * if it's a code `libphonenumber-js` recognizes; (3) a browser-locale
 * guess; (4) 'US'. The user can always override via the selector — this
 * only picks the starting value.
 */
export function guessDefaultDialCountry(opts: {
  existingPhone?: string | null;
  addressCountry?: string | null;
  localeGuess?: string | null;
}): CountryCode {
  const fromExisting = opts.existingPhone ? parsePhoneNumberFromString(opts.existingPhone)?.country : undefined;
  if (fromExisting && isSupportedPhoneCountry(fromExisting)) return fromExisting;

  const addr = opts.addressCountry?.trim().toUpperCase();
  if (addr && isSupportedPhoneCountry(addr)) return addr;
  // Free-text address.country may be a full name ("United States") rather
  // than an ISO code — try a name match against the same option list.
  if (addr) {
    const byName = PHONE_DIAL_CODE_OPTIONS.find((o) => o.name.toUpperCase() === addr);
    if (byName) return byName.country;
  }

  const locale = opts.localeGuess?.trim().toUpperCase();
  if (locale && isSupportedPhoneCountry(locale)) return locale;

  return 'US';
}

/**
 * Reformats `rawValue` for `country` using `AsYouType`, and computes the
 * caret position to restore after the reformat — counts digits before the
 * old caret, then finds the position with the same digit count in the
 * reformatted string. AsYouType already drops any character it can't use
 * (letters, stray punctuation), so a keystroke it rejects is silently
 * absorbed rather than flashed as an error — no special-casing needed here.
 */
export function reformatPhoneWithCaret(
  rawValue: string,
  caret: number,
  country: CountryCode,
): { formatted: string; caret: number } {
  let digitsBeforeCaret = 0;
  for (let i = 0; i < Math.min(caret, rawValue.length); i++) {
    if (/\d/.test(rawValue[i]!)) digitsBeforeCaret++;
  }

  // AsYouType.input() truncates at the first character it can't parse (a
  // stray letter, say) instead of skipping just that one character — so a
  // bad keystroke would silently eat every digit typed after it, not just
  // itself. Pre-filtering to digits (plus one leading '+', so a pasted
  // international value still parses) is what makes an unacceptable
  // character vanish without eating the rest of the number.
  const leadingPlus = rawValue.trimStart().startsWith('+') ? '+' : '';
  const digitsOnly = rawValue.replace(/\D/g, '');
  const formatted = new AsYouType(country).input(leadingPlus + digitsOnly);

  if (digitsBeforeCaret === 0) return { formatted, caret: 0 };

  let count = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i]!)) {
      count++;
      if (count === digitsBeforeCaret) return { formatted, caret: i + 1 };
    }
  }
  return { formatted, caret: formatted.length };
}

/**
 * SIL-1212 — one-keystroke backspace over formatting punctuation. Plain
 * `String.prototype.slice`-out-one-char backspace, run against the
 * formatted display string, does nothing useful when the character right
 * before the caret is a `)`, `-`, or space: that character carries no
 * digit, so removing it and re-running `reformatPhoneWithCaret` reformats
 * the SAME digit string back to the SAME text — the field appears not to
 * respond, and it takes a second backspace (now landing on a real digit) to
 * actually change anything.
 *
 * Call this instead of a plain single-character delete on Backspace: it
 * walks left from the caret past any run of non-digit formatting
 * characters and deletes the digit it finds — so one keystroke always
 * removes exactly one digit, matching what the user sees happen. Pass the
 * result into `reformatPhoneWithCaret` to get back a formatted string +
 * caret.
 */
export function deleteDigitBeforeCaret(rawValue: string, caret: number): { value: string; caret: number } {
  if (caret <= 0) return { value: rawValue, caret };
  let i = caret - 1;
  while (i >= 0 && !/\d/.test(rawValue[i]!)) i--;
  if (i < 0) {
    // No digit anywhere before the caret (e.g. deleting a leading '+' or
    // stray punctuation with nothing typed yet) — fall back to a plain
    // single-character delete so Backspace still does *something*.
    return { value: rawValue.slice(0, caret - 1) + rawValue.slice(caret), caret: caret - 1 };
  }
  return { value: rawValue.slice(0, i) + rawValue.slice(i + 1), caret: i };
}

/**
 * SIL-1212 — detects a pasted international number's country, e.g. a
 * pasted "+44 20 7183 8750" resolves to 'GB', so the caller can switch the
 * dial-code selector to match rather than leaving it on whatever country
 * was previously selected. Returns null for anything that doesn't parse as
 * international with a country `libphonenumber-js` has metadata for.
 */
export function detectCountryFromInternational(value: string): CountryCode | null {
  const trimmed = value.trim();
  if (!trimmed.startsWith('+')) return null;
  const parsed = parsePhoneNumberFromString(trimmed);
  return parsed?.country && isSupportedPhoneCountry(parsed.country) ? parsed.country : null;
}

/**
 * SIL-1212 — a real, validator-accepted example number for `country`
 * (`libphonenumber-js`'s own `examples.mobile.json` metadata), national
 * format, e.g. "(201) 555-0123" for US. Backs the phone field's
 * placeholder — replaces the old fictional "(555) 000-0000", which
 * `isValidPhoneNumber` rejects for every country (it's a movie-prop
 * number). Null only if `libphonenumber-js` has no example for `country`
 * (none observed in practice, but the metadata isn't contractually
 * guaranteed to cover every entry `getCountries()` returns).
 */
export function phoneExampleForCountry(country: CountryCode): string | null {
  const example = getExampleNumber(country, phoneExamples as Examples);
  return example ? example.formatNational() : null;
}

/** Same example number, international format (e.g. "+1 201 555 0123") — backs the server-side hint text. */
export function phoneExampleInternational(country: CountryCode): string | null {
  const example = getExampleNumber(country, phoneExamples as Examples);
  return example ? example.formatInternational() : null;
}

/** Valid for the country (not just "possible" — a plausible-length prefix), per the issue's acceptance criteria. */
export function isValidPhoneForCountry(value: string, country: CountryCode): boolean {
  if (!value.trim()) return false;
  return isValidPhoneNumber(value, country);
}

/** E.164 string if `value` is a valid number for `country`; null otherwise. Never throws. */
export function toE164(value: string, country: CountryCode): string | null {
  if (!value.trim()) return null;
  const parsed = parsePhoneNumberFromString(value, country);
  return parsed && parsed.isValid() ? parsed.number : null;
}

/**
 * National-format display string for an E.164 (or other parseable) value,
 * e.g. "+14155552671" → "(415) 555-2671". Falls back to the raw value
 * unchanged when it doesn't parse — this is the SIL-1198 "no backfill"
 * read path: an old, non-E.164 value must still display (and be editable)
 * even if it can't be reformatted.
 */
export function formatPhoneForDisplay(value: string, country: CountryCode): string {
  const parsed = parsePhoneNumberFromString(value, country) ?? parsePhoneNumberFromString(value);
  return parsed ? parsed.formatNational() : value;
}

/**
 * SIL-1212 — turns a `validatePhoneNumberLength` result into a message that
 * tells a real length problem apart from an invalid pattern. Root cause:
 * `phoneValidationMessage` used to return one fixed "check the digit count"
 * template for every failure, so a pattern failure (e.g. a fictional "555"
 * exchange, which is the RIGHT length for US but not a real number) was
 * reported as a length problem it isn't — `validatePhoneNumberLength`
 * returns `undefined` for that case (the length is fine; nothing else
 * distinguishes it), which is exactly the pattern-failure branch below.
 */
function phoneFailureReasonMessage(
  reason: ValidatePhoneNumberLengthResult | undefined,
  subjectLabel: string,
): string {
  switch (reason) {
    case 'TOO_SHORT':
      return `That doesn't look like a valid ${subjectLabel} number — it's too short (check the digit count).`;
    case 'TOO_LONG':
      return `That doesn't look like a valid ${subjectLabel} number — it's too long (check the digit count).`;
    case 'NOT_A_NUMBER':
      return `That doesn't look like a phone number — check what you entered.`;
    case 'INVALID_COUNTRY':
      return `That doesn't look like a valid phone number for ${subjectLabel} — check the country code.`;
    case 'INVALID_LENGTH':
    default:
      // Length is plausible but the digits don't match any real number for
      // this country — e.g. a "555" exchange in the US. Not a length issue.
      return `That doesn't look like a valid ${subjectLabel} number — check the number itself, not just its length.`;
  }
}

/**
 * Specific, country-named validation message for the client-side
 * blur/plausible-length check — distinguishes TOO_SHORT / TOO_LONG from an
 * invalid pattern (see `phoneFailureReasonMessage`). Returns null when
 * `value` is valid.
 */
export function phoneValidationMessage(value: string, country: CountryCode): string | null {
  if (!value.trim()) return null;
  if (isValidPhoneNumber(value, country)) return null;
  const name = countryDisplayName(country);
  const reason = validatePhoneNumberLength(value, country);
  return phoneFailureReasonMessage(reason, name);
}

export type NormalizePhoneResult = { ok: true; e164: string } | { ok: false; message: string };

/**
 * Server-side normalize-and-validate for a PATCH body's `phone` field. No
 * country is supplied here — the API relies on the value already carrying
 * its own `+<country calling code>` prefix (the customer portal's dial-code
 * selector always produces that before autosave/Save; see individual-
 * profile.tsx). Defense-in-depth for any other caller, same spirit as
 * individual-profile.ts's countryOfResidence ISO-2 check.
 *
 * SIL-1212: the hint now shows a real accepted example number
 * (`phoneExampleInternational`) instead of the fictional "+1 555 000 0000"
 * (`isValidPhoneNumber` rejects that placeholder too — same root cause as
 * the client-side field's old placeholder), and the message distinguishes
 * TOO_SHORT / TOO_LONG from a missing/invalid country code.
 */
export function normalizePhoneToE164(raw: string): NormalizePhoneResult {
  const trimmed = raw.trim();
  const parsed = parsePhoneNumberFromString(trimmed);
  if (parsed && parsed.isValid()) {
    return { ok: true, e164: parsed.number };
  }
  const example = phoneExampleInternational('US');
  const exampleText = example ?? '+1 201 555 0123';
  const reason = validatePhoneNumberLength(trimmed);
  if (reason === 'TOO_SHORT') {
    return {
      ok: false,
      message: `That phone number looks too short — check the digit count. Enter it in international format, e.g. ${exampleText}.`,
    };
  }
  if (reason === 'TOO_LONG') {
    return {
      ok: false,
      message: `That phone number looks too long — check the digit count. Enter it in international format, e.g. ${exampleText}.`,
    };
  }
  return {
    ok: false,
    message: `Enter a valid phone number in international format, e.g. ${exampleText}.`,
  };
}

/**
 * SIL-1506 - server-side destination allow-list for buyer-verification SMS
 * sent through Twilio Verify (SMS-pumping defense, SIL-1313 review #12). The
 * `+1` calling code also covers Caribbean premium-rate pumping targets
 * (+1-876 Jamaica etc.), so the calling code alone is NOT enough: the number
 * must be a valid, parseable NANP number whose libphonenumber country is US
 * or CA, in the strict `+1 [2-9]XX [2-9]XXXXXX` shape (matches the DB regex
 * in migration 997), and not a toll-free or premium-rate area code. Pure
 * logic, no I/O; the API calls this BEFORE any Twilio request.
 */
const TWILIO_VERIFY_SMS_COUNTRIES: ReadonlySet<string> = new Set(['US', 'CA']);
const NANP_STRICT = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
const NANP_TOLL_FREE_OR_PREMIUM_AREA = /^\+1(?:800|833|844|855|866|877|888|900|976)/;

export function isAllowedTwilioVerifySmsPhone(e164: string): boolean {
  if (!NANP_STRICT.test(e164)) return false;
  if (NANP_TOLL_FREE_OR_PREMIUM_AREA.test(e164)) return false;
  const parsed = parsePhoneNumberFromString(e164);
  if (!parsed || !parsed.isValid() || parsed.number !== e164) return false;
  return parsed.country !== undefined && TWILIO_VERIFY_SMS_COUNTRIES.has(parsed.country);
}
